/** @typedef {'service' | 'retail' | 'quick'} PosCatalogMode */

/** @typedef {'ladies' | 'gents'} PosGender */

/**
 * @typedef {object} PosCategory
 * @property {number} id
 * @property {string} name
 * @property {string} [icon]
 */

/**
 * @typedef {object} PosCatalogItem
 * @property {number} id
 * @property {number} category_id
 * @property {string} name
 * @property {number} duration
 * @property {number} price_ladies
 * @property {number} price_gents
 * @property {PosCatalogMode} mode
 */

/**
 * @typedef {object} PosCartLine
 * @property {string} id
 * @property {string} name
 * @property {string} stylist
 * @property {number} duration
 * @property {number} qty
 * @property {number} unit_price
 * @property {number} line_total
 * @property {'pending' | 'in_progress' | 'done'} status
 * @property {string[]} [badges]
 */

/**
 * @typedef {object} PosCustomer
 * @property {string} name
 * @property {string} phone
 * @property {PosGender} gender
 * @property {number} loyalty_points
 * @property {string} [package_label]
 */

/**
 * @typedef {object} PosTotals
 * @property {number} subtotal
 * @property {number} discount
 * @property {number} vat
 * @property {number} tip
 * @property {number} grand_total
 * @property {number} balance_due
 */

export const POS_MODES = [
  { id: 'walk_in', label: 'Walk-in', shortcut: 'Default' },
  { id: 'appointment', label: 'Appointment', shortcut: 'F3' },
  { id: 'package_sale', label: 'Package', shortcut: null },
  { id: 'retail_only', label: 'Retail', shortcut: null },
]

export const POS_CATALOG_MODES = [
  { id: 'service', label: 'Services' },
  { id: 'retail', label: 'Retail' },
  { id: 'quick', label: 'Quick keys' },
]

export const POS_CATEGORIES = [
  { id: 1, name: 'Hair', icon: '✂️' },
  { id: 2, name: 'Nails', icon: '💅' },
  { id: 3, name: 'Facial', icon: '✨' },
  { id: 4, name: 'Massage', icon: '🌸' },
  { id: 5, name: 'Waxing', icon: '🪷' },
]

export const POS_CATALOG_ITEMS = [
  {
    id: 101,
    category_id: 1,
    name: 'Blow Dry & Style',
    duration: 45,
    price_ladies: 120,
    price_gents: 0,
    mode: 'service',
  },
  {
    id: 102,
    category_id: 1,
    name: 'Keratin Treatment',
    duration: 120,
    price_ladies: 450,
    price_gents: 0,
    mode: 'service',
  },
  {
    id: 103,
    category_id: 2,
    name: 'Gel Manicure',
    duration: 60,
    price_ladies: 95,
    price_gents: 0,
    mode: 'service',
  },
  {
    id: 104,
    category_id: 3,
    name: 'Hydra Facial',
    duration: 75,
    price_ladies: 380,
    price_gents: 0,
    mode: 'service',
  },
  {
    id: 105,
    category_id: 4,
    name: 'Aromatherapy Massage',
    duration: 60,
    price_ladies: 280,
    price_gents: 0,
    mode: 'service',
  },
  {
    id: 201,
    category_id: 0,
    name: 'Moroccan Oil Serum',
    duration: 0,
    price_ladies: 145,
    price_gents: 145,
    mode: 'retail',
  },
  {
    id: 202,
    category_id: 0,
    name: 'Luxury Hand Cream',
    duration: 0,
    price_ladies: 85,
    price_gents: 85,
    mode: 'retail',
  },
  {
    id: 301,
    category_id: 0,
    name: 'Express Blow Dry',
    duration: 30,
    price_ladies: 80,
    price_gents: 0,
    mode: 'quick',
  },
  {
    id: 302,
    category_id: 0,
    name: 'Threading — Brow',
    duration: 15,
    price_ladies: 35,
    price_gents: 0,
    mode: 'quick',
  },
]

export const POS_CART_LINES = [
  {
    id: 'line-1',
    name: 'Hydra Facial',
    stylist: 'Sara Al-Mansoori',
    duration: 75,
    qty: 1,
    unit_price: 380,
    line_total: 380,
    status: 'in_progress',
    badges: ['Add-on: LED mask'],
  },
  {
    id: 'line-2',
    name: 'Gel Manicure',
    stylist: 'Layla Hassan',
    duration: 60,
    qty: 1,
    unit_price: 95,
    line_total: 95,
    status: 'pending',
    badges: [],
  },
]

export const POS_CUSTOMER = {
  name: 'Amira Al-Farsi',
  phone: '+971 50 123 4567',
  gender: 'ladies',
  loyalty_points: 1240,
  package_label: 'Glow Combo — 2 visits left',
}

export const POS_TOTALS = {
  subtotal: 475,
  discount: 0,
  vat: 23.75,
  tip: 0,
  grand_total: 498.75,
  balance_due: 498.75,
}

export const POS_SALE_META = {
  sale_number: 'POS-2026-0042',
  branch_label: 'Dubai Marina',
  opened_at: '14:32',
}

export const POS_SHORTCUTS = [
  { key: 'F2', labelKey: 'pos.shortcuts.customer' },
  { key: 'F3', labelKey: 'pos.shortcuts.appointment' },
  { key: 'F4', labelKey: 'pos.shortcuts.pay' },
  { key: 'F5', labelKey: 'pos.shortcuts.hold' },
  { key: 'F6', labelKey: 'pos.shortcuts.package' },
  { key: 'F7', labelKey: 'pos.shortcuts.redeem' },
  { key: 'F10', labelKey: 'pos.shortcuts.recall' },
  { key: '+/-', labelKey: 'pos.shortcuts.qty' },
  { key: 'Esc', labelKey: 'pos.shortcuts.close' },
]

/**
 * @param {PosCatalogItem} item
 * @param {PosGender} gender
 */
export function resolveCatalogPrice(item, gender) {
  return gender === 'gents' ? item.price_gents : item.price_ladies
}

/**
 * @param {PosCatalogItem[]} items
 * @param {PosCatalogMode} mode
 * @param {number | null} categoryId
 */
export function filterCatalogItems(items, mode, categoryId) {
  return items.filter((item) => {
    if (item.mode !== mode) return false
    if (mode === 'service' && categoryId !== null) {
      return item.category_id === categoryId
    }
    return true
  })
}
